import React, { useState, useEffect } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models, IEmbedConfiguration } from "powerbi-client";
import styles from "./PowerBI.module.scss";
import { formatName, getCombinedFilters } from "./utils";
import { Select, Alert, Spin } from "antd";
import notifier from "utils/notifier";
import FloatLabel from "components/Common/FloatLabel";
import { ErrorResponse } from "utils/types";

const { Option } = Select;

interface PowerBIReportProps {
  getDistinct(userId: string): any;
  getAccessToken(): any;
  getPowerBIProvider(payload: any): any;
  isFetchingDistinct: boolean;
  isFetchingProviders: boolean;
  isFetchingToken: boolean;
}

const PowerBIReport = (props: PowerBIReportProps) => {
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [providerList, setProviderList] = useState<any>([]);
  const [vendorList, setVendorList] = useState<any>([]);
  const [fullList, setFullList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [embedConfig, setEmbedConfig] = useState<IEmbedConfiguration>();
  const [PBIConfig, setPBIConfig] = useState("");
  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    getDistinctProviders();
  }, []);

  useEffect(() => {
    if (!PBIConfig) return;
    try {
      const pbiConifg = JSON.parse(PBIConfig);
      const filters = getCombinedFilters(selectedProvider, selectedVendor);
      const newConfig: IEmbedConfiguration = {
        type: "report",
        id: pbiConifg.ReportId,
        embedUrl: pbiConifg.EmbedUrl,
        accessToken: pbiConifg.Token,
        tokenType: models.TokenType.Embed,
        settings: {
          filterPaneEnabled: false,
          navContentPaneEnabled: false,
        },
        filters: filters,
      };

      setEmbedConfig(newConfig);
    } catch (error) {
      notifier.error("Failed to parse PBIConfig or set embed config");
    }
  }, [selectedProvider, selectedVendor, PBIConfig]);

  const handleSelectChange = (selectedValue: string | undefined) => {
    if (selectedValue) {
      const provider = JSON.parse(selectedValue);
      setSelectedProvider(selectedValue);
      const filteredVendors = fullList.filter(
        (vendor: any) => vendor.RmiProvKeyId === provider.RmiProvKeyId
      );
      // Check if any providers matched the filter
      if (filteredVendors.length > 0) {
        setSelectedVendor(JSON.stringify(filteredVendors[0]));
      }
    }
  };

  const handleVendorSelectChange = (selectedValue: string | undefined) => {
    if (selectedValue) {
      try {
        // Attempt to parse the selected value as JSON
        const vendor = JSON.parse(selectedValue);
        setSelectedVendor(selectedValue);

        // Filter providers based on the selected vendor ID
        const filteredProviders = fullList.filter(
          (provider: any) => provider.RmiVendorId === vendor.RmiVendorId
        );

        // Check if any providers matched the filter
        if (filteredProviders.length > 0) {
          setProviderList(filteredProviders);
          setSelectedProvider(JSON.stringify(filteredProviders[0]));
        }
      } catch (error) {
        notifier.error(
          "Failed to process the selected vendor. Please check the data format."
        );
      }
    }
  };

  const getDistinctProviders = () => {
    return props
      .getDistinct(userId!)
      .then((providers: any[]) => {
        setIsLoading(true);
        const formattedProviders = formatName(providers);
        const pcps = formattedProviders.filter((prov: any) => {
          const isDesiredClass = ["D", "P", "3C", "C"].includes(prov.RmiClass);
          const hasAccessDays =
            prov.RmiClaimAccessDays === null ||
            parseInt(prov.RmiClaimAccessDays) > 0;
          return isDesiredClass && hasAccessDays;
        });

        // Using a Set to ensure uniqueness of providerKeyIds
        const uniqueProviderKeyIds = new Set();

        pcps.forEach((pcp: any) => {
          if (!uniqueProviderKeyIds.has(pcp.rmiProvKeyId)) {
            uniqueProviderKeyIds.add(pcp.rmiProvKeyId);
          }
        });

        if (uniqueProviderKeyIds.size > 0) {
          const providerKeyIds = Array.from(uniqueProviderKeyIds);
          props
            .getPowerBIProvider({ provKeyIds: providerKeyIds })
            .then((providers: any[]) => {
              const uniqueProviders = new Map();
              providers.forEach((provider) => {
                uniqueProviders.set(provider.RmiProvKeyId, provider);
              });
              
              // Convert Map values to array and sort by RmiRevFullName
              const sortedProviders = Array.from(uniqueProviders.values()).sort((a, b) => {
                return a.RmiRevFullName.localeCompare(b.RmiRevFullName);
              });
              const sortedVendors = Array.from(uniqueProviders.values()).sort((a, b) => {
                return a.RmiVendorName.localeCompare(b.RmiVendorName);
              });

              setProviderList(sortedProviders);
              setVendorList(sortedVendors);
              setFullList(sortedProviders);

              // Setting the first provider as selected
              if (sortedProviders.length > 0) {
                const firstPcp = JSON.stringify(sortedProviders[0]);
                setSelectedVendor(firstPcp);
                setSelectedProvider(firstPcp);
              }   
            });
          props.getAccessToken().then((res: any) => {
            setPBIConfig(JSON.stringify(res));
          });
          setIsLoading(false);
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  return (
    <div>
      <div
        className={styles["PBIcontainer"]}
      >
        <div className={styles["dropdownContainer"]}>
        <label className={styles["floatingLabelStyle"]}>
          Vendor:
        </label>
          <Select
            className={styles.dropdown}
            showSearch
            loading={isLoading}
            placeholder=""
            onSelect={(value) => handleVendorSelectChange(value)}
            value={selectedVendor}
            style={{ width: "100%" }}
          >
            {vendorList.map((option: any, index: any) => (
              <Option key={index} value={JSON.stringify(option)}>
                {`${option.RmiVendorName} - ${option.RmiVendorId}`}
              </Option>
            ))}
          </Select>
        </div>
        <div className={styles["dropdownContainer"]}>
        <label className={styles["floatingLabelStyle"]}>
          Provider:
        </label>
          <Select
            className={styles.dropdown}
            showSearch
            loading={isLoading}
            placeholder=""
            onSelect={(value) => handleSelectChange(value)}
            value={selectedProvider}
            style={{ width: "100%" }}
          >
            {providerList.map((option: any, index: any) => (
              <Option key={index} value={JSON.stringify(option)}>
                {`
                    ${option.RmiCompanyId} - 
                    ${option.RmiRevFullName} - (${
                  option.RmiCity ? option.RmiCity : "N/A"
                }${option.RmiStreet ? ` ${option.RmiCity}` : ""})
                 `}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      {embedConfig && !isLoading && (
        <div className={styles["reportContainer"]}>
          <PowerBIEmbed
            embedConfig={embedConfig}
            cssClassName={styles.reportstyleclass}
          />
        </div>
      )}
    </div>
  );
};

export default PowerBIReport;
